import React, { useContext, useEffect, useMemo } from "react";
import { Card, Switch, message, Typography } from "antd";
import { SafetyOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../context/AuthProvider";
import SettingsModel from "../../lib/firebase/settingsModel";
import { propertyKeys } from "../../lib/queryKeys";
import { trackEvent } from "../../helpers/mixpanel";
import { accountRequiresLockedHighCompliance } from "../../helpers/compliance";
import styled from "styled-components";

const { Text } = Typography;

const SettingsCard = styled(Card)`
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &.section-card {
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 16px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04) !important;
    margin-bottom: 24px !important;
    overflow: hidden !important;
  }

  /* Align with Settings.jsx so section-card-header negative margins fit */
  &.section-card .ant-card-body {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    &.section-card .ant-card-body {
      padding: 1rem;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-border-light);
  text-align: left;
  position: relative;

  /* Match Settings.jsx section cards: green bar, white text */
  &.section-card-header {
    align-items: center !important;
    background: var(--gradient-primary) !important;
    padding: 1.75rem 2rem 1.25rem !important;
    margin: -2rem -2rem 1.5rem -2rem !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 14px 14px 0 0;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);

    @media (max-width: 768px) {
      margin: -1rem -1rem 1.5rem -1rem !important;
      padding: 1.5rem 1.25rem 1.1rem !important;
    }

    h3 {
      color: #ffffff !important;
      font-size: 1.125rem !important;
      font-weight: 600 !important;
      line-height: 1.35 !important;
      letter-spacing: -0.01em;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    }

    p {
      color: rgba(255, 255, 255, 0.92) !important;
      font-size: 0.9rem !important;
      line-height: 1.45 !important;
    }

    & > .anticon {
      color: #ffffff !important;
      filter: brightness(0) invert(1) !important;
    }

    h3 .anticon {
      filter: none !important;
      color: rgba(255, 255, 255, 0.92) !important;
    }

    h3 .anticon svg {
      fill: currentColor !important;
    }
  }
`;

const CardTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
  text-align: left;

  .section-card-header & {
    color: #ffffff !important;
    line-height: 1.35 !important;
  }

  .section-card-header & span {
    color: inherit !important;
  }
`;

const CardDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  line-height: 1.45;
  color: #6b7280;
  margin: 0;
  text-align: left;

  .section-card-header & {
    color: rgba(255, 255, 255, 0.92) !important;
    line-height: 1.45 !important;
  }
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingContent = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
  @media (min-width: 769px) {
    max-width: 90%;
  }
`;

const SettingLabel = styled.div`
  font-weight: 500;
  color: var(--color-text, #1a1a1a);
  text-align: left;
`;

const SettingHelp = styled.div`
  font-size: 0.8125rem;
  color: var(--color-text-light, #666);
  margin-top: 0.25rem;
  line-height: 1.4;
  text-align: left;
`;

const DesktopBr = styled.br`
  @media (max-width: 768px) {
    display: none;
  }
`;

/**
 * Landlord "Compliance Toggle" (Hybrid Compliance).
 * When ON: medical-data guardrails + HITL for adverse decisions apply to ALL properties in the account.
 * If any property/door is in a mandatory state (WA, NV, CT, IN, KY), the toggle stays ON and cannot be turned off.
 * Otherwise defaults to ON with opt-out (stored false).
 */
const ComplianceSettings = ({ properties = [], units = [] }) => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { data: userSettings, isLoading } = useQuery({
    queryKey: propertyKeys.settings(user?.uid),
    queryFn: () => SettingsModel.getOrCreateSettings(),
    enabled: !!user?.uid,
  });

  const requiresLockedCompliance = useMemo(
    () => accountRequiresLockedHighCompliance(properties, units),
    [properties, units]
  );

  /** Default ON when unset; explicit `false` in Firestore means user opted out. */
  const storedHighCompliance = userSettings?.highComplianceMode;
  const highComplianceEffectiveOn =
    requiresLockedCompliance || storedHighCompliance !== false;

  useEffect(() => {
    if (!user?.uid || !requiresLockedCompliance) return;
    if (storedHighCompliance === true) return;
    let cancelled = false;
    (async () => {
      try {
        await SettingsModel.updateSettings(user.uid, {
          highComplianceMode: true,
        });
        if (!cancelled) {
          queryClient.setQueryData(propertyKeys.settings(user.uid), (prev) => ({
            ...prev,
            highComplianceMode: true,
          }));
          trackEvent("compliance_setting_change", {
            highComplianceMode: true,
            reason: "mandatory_state_asset",
          });
        }
      } catch (e) {
        console.warn(
          "[ComplianceSettings] Failed to enforce high-compliance lock:",
          e
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, requiresLockedCompliance, storedHighCompliance, queryClient]);

  const handleHighComplianceChange = async (checked) => {
    if (!user?.uid || requiresLockedCompliance) return;
    try {
      await SettingsModel.updateSettings(user.uid, {
        highComplianceMode: checked,
      });
      queryClient.setQueryData(propertyKeys.settings(user.uid), (prev) => ({
        ...prev,
        highComplianceMode: checked,
      }));
      message.success(
        checked
          ? "High-Compliance Mode enabled"
          : "High-Compliance Mode disabled"
      );
      trackEvent("compliance_setting_change", { highComplianceMode: checked });
    } catch (e) {
      message.error(e?.message || "Failed to save");
    }
  };

  if (!user?.uid) return null;

  return (
    <SettingsCard id="compliance-settings" className="section-card">
      <CardHeader className="section-card-header">
        <SafetyOutlined style={{ fontSize: "24px" }} />
        <div>
          <CardTitle>Compliance</CardTitle>
          <CardDescription>
            Washington (MHMDA/SB 6284), Nevada (SB 370), Connecticut, and other
            states with consumer health data laws. Nationwide medical data
            handling.
          </CardDescription>
        </div>
      </CardHeader>
      <SettingItem>
        <SettingContent>
          <SettingLabel>
            Enable High-Compliance Mode (MHMDA & similar laws)
          </SettingLabel>
          <SettingHelp>
            Mandatory in some states (e.g. Washington, Nevada, Connecticut).
            Enables medical data blocking and human-in-the-loop review for
            adverse decisions.
            <DesktopBr />
            When ON, these rules apply to all properties in your account.
            {requiresLockedCompliance ? (
              <>
                <DesktopBr />
                <Text strong>
                  Required for your account: at least one property or door is in
                  a state with these laws. This cannot be turned off.
                </Text>
              </>
            ) : null}
          </SettingHelp>
        </SettingContent>
        <Switch
          checked={highComplianceEffectiveOn}
          onChange={handleHighComplianceChange}
          loading={isLoading}
          disabled={requiresLockedCompliance || isLoading}
        />
      </SettingItem>
    </SettingsCard>
  );
};

export default ComplianceSettings;
